/**
 * API client for dust swap operations
 *
 * Handles communication with backend for creating swap orders
 * and generating partially signed transactions.
 */

import config from '@/config';

const BASE_URL = config.API_BASE_URL;

/**
 * Serialized instruction format matching backend's InnerIxDto
 */
export interface SerializedInstruction {
  programId: string;
  keys: Array<{
    pubkey: string;
    isSigner: boolean;
    isWritable: boolean;
  }>;
  data: string; // base64 encoded
}

/**
 * Request body for creating a swap order
 * Matches backend's SwapIxDto
 */
export interface SwapOrderRequest {
  tokenAccounts: string[];
  tokenSwappedAmounts: number[];
  addressLookupTableAccounts: string[];
  instructions: SerializedInstruction[];
}

/**
 * Response from create swap order endpoint
 */
export interface SwapOrderResponse {
  code: number;
  data: string; // order_id
  message?: string;
}

/**
 * Response from generate signature endpoint
 */
export interface GenerateSignatureResponse {
  code: number;
  data: {
    order_id: string;
    serialized_tx: string; // Hex-encoded partially signed transaction
  };
  message?: string;
}

/**
 * Create a swap order on the backend
 *
 * @param request - Swap order request containing instructions and token info
 * @returns Order ID on success
 */
export async function createSwapOrder(request: SwapOrderRequest): Promise<SwapOrderResponse> {
  console.log('[SWAP-API] Creating swap order for', request.tokenAccounts.length, 'token(s)');
  console.log('[SWAP-API] Request details:', {
    tokenAccounts: request.tokenAccounts,
    tokenSwappedAmounts: request.tokenSwappedAmounts,
    addressLookupTableCount: request.addressLookupTableAccounts.length,
    instructionCount: request.instructions.length,
  });
  console.log('[SWAP-API] Full request body:', JSON.stringify(request, null, 2));

  const response = await fetch(`${BASE_URL}/chest/swap-tokens`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  console.log('[SWAP-API] createSwapOrder response status:', response.status, response.statusText);

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required. Please sign in with your wallet.');
    }

    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      console.error('[SWAP-API] createSwapOrder error response:', errorData);
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(`Failed to create swap order: ${errorMessage}`);
  }

  const result = await response.json();
  console.log('[SWAP-API] createSwapOrder success:', result);
  return result;
}

/**
 * Generate a partially signed transaction for a swap order
 *
 * Uses the same endpoint as burn operations.
 *
 * @param orderId - Order ID from createSwapOrder
 * @returns Hex-encoded partially signed transaction
 */
export async function generateSwapSignature(orderId: string): Promise<GenerateSignatureResponse> {
  console.log('[SWAP-API] Generating signature for order:', orderId);
  console.log('[SWAP-API] Request URL:', `${BASE_URL}/tx-order/generate-signature?order_id=${encodeURIComponent(orderId)}`);

  const response = await fetch(
    `${BASE_URL}/tx-order/generate-signature?order_id=${encodeURIComponent(orderId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }
  );

  console.log('[SWAP-API] generateSwapSignature response status:', response.status, response.statusText);
  console.log('[SWAP-API] generateSwapSignature response headers:', Object.fromEntries(response.headers.entries()));

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required. Please sign in with your wallet.');
    }

    let errorMessage = response.statusText;
    let errorData: any;
    try {
      errorData = await response.json();
      console.error('[SWAP-API] generateSwapSignature error response:', errorData);
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch (e) {
      // Try to get raw text
      try {
        const errorText = await response.text();
        console.error('[SWAP-API] generateSwapSignature error text:', errorText);
      } catch {
        // Ignore
      }
    }
    throw new Error(`Failed to generate swap signature: ${errorMessage}`);
  }

  const result = await response.json();
  console.log('[SWAP-API] generateSwapSignature success:', {
    orderId: result.data?.order_id,
    hasTx: !!result.data?.serialized_tx,
    txLength: result.data?.serialized_tx?.length,
    needsWrapup: result.data?.needs_wrapup,
  });
  return result;
}

/**
 * Generate a partially signed wrapUp transaction for completing a swap
 *
 * This should be called AFTER the swap transaction has confirmed on-chain.
 * The wrapUp transaction:
 * - Closes the swapped token accounts (returns rent to user PDA)
 * - Takes the platform fee (10% of swap + token_fees_bps of rent)
 * - Updates user's balance in user_info PDA
 *
 * @param orderId - Order ID from createSwapOrder
 * @returns Hex-encoded partially signed wrapUp transaction
 */
export async function generateWrapUpSignature(orderId: string): Promise<GenerateSignatureResponse> {
  console.log('[SWAP-API] Generating wrapUp signature for order:', orderId);
  console.log('[SWAP-API] Request URL:', `${BASE_URL}/tx-order/generate-wrapup-signature?order_id=${encodeURIComponent(orderId)}`);

  const response = await fetch(
    `${BASE_URL}/tx-order/generate-wrapup-signature?order_id=${encodeURIComponent(orderId)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }
  );

  console.log('[SWAP-API] generateWrapUpSignature response status:', response.status, response.statusText);

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication required. Please sign in with your wallet.');
    }

    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      console.error('[SWAP-API] generateWrapUpSignature error response:', errorData);
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch (e) {
      try {
        const errorText = await response.text();
        console.error('[SWAP-API] generateWrapUpSignature error text:', errorText);
      } catch {
        // Ignore
      }
    }
    throw new Error(`Failed to generate wrapUp signature: ${errorMessage}`);
  }

  const result = await response.json();
  console.log('[SWAP-API] generateWrapUpSignature success:', {
    orderId: result.data?.order_id,
    hasTx: !!result.data?.serialized_tx,
    txLength: result.data?.serialized_tx?.length,
  });
  return result;
}
